'use client'

import { useForm } from 'react-hook-form'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useTurnstile } from '@/Turnstile'
import { Button } from '@/General/Button'
import { useSubmitTrickMutation } from '@/generated/graphql'
import { useRouter } from 'next/navigation'
import { useRef, useEffect } from 'react'
import { useStore } from '@/store'
import { EMAIL_REGEX } from '@/validation'

type SubmitTrickFormData = {
  name: string
  email: string
  trickName: string
  description: string
  websiteUrl?: string
  videoUrl?: string
}

export const SubmitTrickForm = () => {
  const user = useStore(state => state.user)
  const router = useRouter()
  const pendingThankYouRef = useRef<SubmitTrickFormData | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<SubmitTrickFormData>({
    defaultValues: {
      name: '',
      email: '',
      trickName: '',
      description: '',
      websiteUrl: '',
      videoUrl: '',
    },
  })

  // Prepopulate form with user data when logged in
  useEffect(() => {
    if (user) {
      const userName = user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`.trim()
        : user.firstName || user.lastName || ''

      reset({
        name: userName,
        email: user.email,
        trickName: '',
        description: '',
        websiteUrl: '',
        videoUrl: '',
      })
    }
  }, [user, reset])

  const { executeTurnstile } = useTurnstile()

  const { mutate: submitTrick } = useSubmitTrickMutation({
    onSuccess: (result) => {
      const pending = pendingThankYouRef.current
      if (result.submitTrick && pending) {
        pendingThankYouRef.current = null
        const params = new URLSearchParams({
          email: pending.email,
        })
        router.push(`/submit-a-trick/thank-you?${params.toString()}`)
      }
    },
    onError: () => {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    },
  })

  const onSubmit = async (data: SubmitTrickFormData) => {
    try {
      const token = await executeTurnstile()
      const response = await fetch('/api/verifycaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })

      if (!response.ok) {
        setError('root', {
          type: 'manual',
          message:
            'Unfortunately we were unable to verify that a human was submitting this form. Please try emailing us at magic@vanishingincmagic.com instead.',
        })
        return
      }

      pendingThankYouRef.current = data
      submitTrick({
        name: data.name,
        email: data.email,
        trickName: data.trickName,
        description: data.description,
        websiteUrl: data.websiteUrl || null,
        videoUrl: data.videoUrl || null
      })
    } catch {
      setError('root', {
        type: 'manual',
        message: 'Something went wrong. Please try again later or email us at magic@vanishingincmagic.com.',
      })
    }
  }

  return (
    <form
      onSubmit={e => void handleSubmit(onSubmit)(e)}
      className="needs-validation"
      noValidate
    >
        <div className="flex flex-wrap">
          <div className="w-full md:w-2/3">
            <ErrorBox
              errors={
                Object.values(errors)
                  .map(error => error?.message)
                  .filter(Boolean) as string[]
              }
            />

            <div className="mb-4">
              <input
                {...register('name', {
                  required: 'Please enter your name',
                })}
                type="text"
                id="name"
                placeholder="Your name"
                className={`block w-full px-3 py-2 border ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <input
                {...register('email', {
                  required: 'Please enter your email address',
                  pattern: {
                    value: EMAIL_REGEX,
                    message: 'Invalid email address',
                  },
                })}
                type="email"
                id="email"
                placeholder="Your email address"
                className={`block w-full px-3 py-2 border ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <input
                {...register('trickName', {
                  required: 'Please enter the name of your trick or project',
                })}
                type="text"
                id="trickName"
                placeholder="Name of trick / project"
                className={`block w-full px-3 py-2 border ${
                  errors.trickName ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg`}
              />
              {errors.trickName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.trickName.message}
                </p>
              )}
            </div>

            <div className="mb-4">
              <input
                {...register('websiteUrl')}
                type="url"
                id="websiteUrl"
                placeholder="Your website (optional)"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>

            <div className="mb-4">
              <input
                {...register('videoUrl')}
                type="url"
                id="videoUrl"
                placeholder="Video URL (optional)"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg"
              />
            </div>

            <div className="mb-4">
              <textarea
                {...register('description', {
                  required: 'Please enter some information about your trick',
                })}
                id="description"
                rows={8}
                placeholder="Details about your trick"
                className={`block w-full px-3 py-2 border ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                } rounded-md shadow-sm focus:outline-hidden focus:ring-green-500 focus:border-green-500 text-lg`}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.description.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full">
              Submit enquiry
            </Button>
                      </div>

          <div className="w-full md:w-1/3 pl-0 md:pl-4">
            <div className="bg-yellow-100 text-yellow-700 px-4 py-3 rounded">
              <p className="font-bold mb-2">Tips for the best submission</p>

              <ul className="list-disc pl-5">
                <li className="mb-2">Tell us about yourself</li>
                <li className="mb-2">
                  Include a link to a video of the trick (example: Dropbox or
                  unlisted YouTube links)
                </li>
                <li className="mb-2">Include your website if you have one</li>
                <li className="mb-2">Include a brief explanation</li>
                <li className="mb-2">Include any crediting information</li>
                <li className="mb-2">
                  Tell us why you think it's a good fit for us
                </li>
                <li className="mb-2">
                  Wait until the trick is ready &mdash; we prefer complete tricks
                  over early ideas
                </li>
              </ul>
            </div>
          </div>
        </div>
      </form>
  )
}
